import React, { useContext, useState } from 'react';
import { InputField, RadioGroupField } from '@trp-beacon-ui-kit/react-controls';
import { FlowContext } from '../../core/flow-engine';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import Heading from '../../components/Typography/Heading/Heading';
import SubHeading from '../../components/Typography/SubHeading/SubHeading';
import {
  renderAdditionalSectionTable,
  getChosenFundAgreementId,
  checkIfChosenFundIsInExistingFund
} from './util';
import {
  formatAmountForDisplay,
  formatAgreementTypeForDisplay,
  sumOf
} from '../../flows/shared/utils';
import styles from './style.css';
import { isEmptyObject, withDefault } from '../../core/util';

const TrustExistingAgreementsScreen = () => {
  const { appState, next, back, flowProps } = useContext(FlowContext);
  const {
    store: { existingAgreements = [], chosenInvestments }
  } = appState;
  const { title = 'Select source of funds' } = flowProps;

  // chosenInvestments will be utilized when coming back to screen from allocation screen
  // fixes issue where radio button is defaulted when navigating back, but selectedAccount doesn't persist after navigation
  // causing application to crash when continuing to allocations screen without selecting a new agreement option
  const [selectedAccount, setSelectedAccount] = useState(
    withDefault(chosenInvestments, false)
  );
  const [allocationChoice, setAllocationChoice] = useState('FULL');

  // check if there is a already existing selected account. If so, default to that one.
  const { chosen } = appState;

  // chosen can also be a string so we want to make sure we are working with an array here
  const chosenFundAgreementId = getChosenFundAgreementId(
    selectedAccount,
    chosen
  );

  const isChosenFundInExistingFunds = checkIfChosenFundIsInExistingFund(
    existingAgreements,
    chosenFundAgreementId
  );

  if (!selectedAccount && isChosenFundInExistingFunds) {
    const chosenAccount = Object.values(existingAgreements)
      .flat()
      .filter(f => f.agreementID === chosenFundAgreementId);
    setSelectedAccount(chosenAccount);
  }

  const showSelectedSection = id => selectedAccount[0]?.agreementID === id;

  return (
    <div className="container">
      <Heading text={title} />
      <SubHeading text="You'll see your pre-filled change of ownership form on the confirmation page. Mail it in to finalize the transfer to your new account." />

      <div className="accounts-container">
        {existingAgreements &&
          !isEmptyObject(existingAgreements) &&
          Object.values(existingAgreements).map(accountGroup => {
            // get existing account type from first index in each group
            const accountType = formatAgreementTypeForDisplay(
              accountGroup[0]?.agreementType
            );
            const totalAccountBalance = sumOf(
              accountGroup.map(account => account.balanceAsOf)
            );

            return (
              /* eslint-disable react/jsx-key */
              <div className="account-row-container">
                {/* eslint-enable react/jsx-key */}
                <div className="account-row">
                  <InputField
                    checked={accountGroup
                      .map(f => f.agreementID)
                      .includes(chosenFundAgreementId)}
                    key="agreement-group"
                    type="radio"
                    name="accountGroupSelection"
                    data-name="accountGroupSelection"
                    onClick={() => setSelectedAccount(accountGroup)}
                  />
                  <div className="account-owners-cell">
                    <p>{accountType}</p>
                    <p data-name="agreementOwnerName">
                      {accountGroup[0].agreementParties?.map(
                        (person, personIndex) =>
                          personIndex > 0
                            ? ` + ${person.name?.firstName}`
                            : person.name?.firstName
                      )}
                    </p>
                  </div>
                  <span className="account-balance-cell">
                    {formatAmountForDisplay(totalAccountBalance)}
                  </span>
                </div>

                {showSelectedSection(accountGroup[0].agreementID) && (
                  <>
                    <div className="allocation-radio-row">
                      <RadioGroupField
                        title=""
                        key="agreement-group-alllocation"
                        name="accountAllocation"
                        options={[
                          {
                            title: 'Use all funds',
                            value: 'ALL',
                            defaultChecked: true
                          },
                          { title: 'Select certain funds', value: 'SELECTIVE' }
                        ]}
                        onChange={e => setAllocationChoice(e?.target?.value)}
                      />
                    </div>
                    {renderAdditionalSectionTable(
                      accountGroup,
                      accountType,
                      totalAccountBalance
                    )}
                  </>
                )}
              </div>
            );
          })}
      </div>
      <div className="button-container">
        <MFBeaconButton
          variant="primary"
          size="normal"
          onClick={next(selectedAccount, allocationChoice)}
          id="trustExistingAgreementContinue"
          disabled={!selectedAccount.length}
          label="Continue"
        />
        <MFBeaconButton
          variant="secondary"
          size="normal"
          onClick={back()}
          className="beacon-button-margin-override"
          id="trustExistingAgreementBack"
          label="Back"
        />
        <PhoneNumber screen="InvestmentScreen" />
      </div>
      <style jsx>{styles}</style>
    </div>
  );
};

export default TrustExistingAgreementsScreen;
